import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { findValue, FormikSubFieldProps, useFormikFieldStyles } from './FormikField'
import { Field, Input } from '@fluentui/react-components'
import { InputProps } from '@fluentui/react-input'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import * as UUID from 'uuid'
import { Currency } from '../../config/i18nConfig'

export type FormikMoneyFieldProps<Values> = FormikSubFieldProps<InputProps, Values> & {
    currency?: Currency
    decimals?: number
    allowNegative?: boolean
}

function formatToValue<Values>(
    value: string = '',
    props: FormikMoneyFieldProps<Values>,
    fromSeparator: string,
    toSeparator: string,
    strict: boolean,
): string {
    if ((!props.required && !value) || value === '') {
        return ''
    }

    //make sure the value is a string
    value = `${value}`

    const decimalLength = props.currency?.decimals ?? props.decimals ?? 2

    //Only accept `fromSeparator` as decimal separator
    const decimalPosition = value.lastIndexOf(fromSeparator)

    let numberValue = value
    let decimalValue = ''
    if (decimalPosition >= 0) {
        numberValue = numberValue.substring(0, decimalPosition)
        decimalValue = value.substring(decimalPosition + 1)
    }

    //Parse numberValue
    numberValue = numberValue
        .replace(/[^\d-]/, '') // Only accept numbers and dashes.
        .replace(/^[0]+([\d]+)/, '$1') // Remove excess preceding 0

    if (!props.allowNegative) {
        numberValue = numberValue.replace('-', '')
    }
    if (numberValue === '') {
        numberValue = '0'
    }

    //Parse decimalValue
    if (strict) {
        decimalValue = (decimalValue || '').padEnd(decimalLength, '0')
    }
    if (decimalValue && decimalValue.length > decimalLength) {
        decimalValue = decimalValue.substring(0, decimalLength)
    }

    //Concat both values
    value = numberValue
    if (decimalValue || decimalPosition >= 0) {
        value = value + toSeparator
    }
    if (decimalValue) {
        value = value + decimalValue
    }

    return value
}

export default function FormikMoneyField<Values>(props: FormikMoneyFieldProps<Values>): ReactElement {
    const styles = useFormikFieldStyles()
    const { t: translate } = useTranslation()

    const { values, errors, setFieldValue } = useFormikContext<Values>()

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])

    const currentDisplayValue = useMemo(() => formatToValue(findValue(values, props.fieldName as string), props, '.', ',', false) || '', [values])

    const itemRef = useState<string>(UUID.v4())

    return (
        <Field
            label={props.labelKey ? translate(props.labelKey) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey) : undefined)}
            {...props}
            validationMessage={validationMessage}
            validationState={validationState}
            className={props.className ?? (!props.renderLabel && !props.labelKey && styles.noLabel)}
        >
            <Input
                itemRef={itemRef}
                required={props.required}
                onChange={(e, data) => {
                    const value = formatToValue(data.value, props, ',', '.', false)
                    setFieldValue(props.fieldName as string, value)
                }}
                onBlur={e => {
                    const value = formatToValue(e.currentTarget.value, props, ',', '.', true)
                    setFieldValue(props.fieldName as string, value)
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter') {
                        const value = formatToValue(e.currentTarget.value, props, ',', '.', true)
                        setFieldValue(props.fieldName as string, value)
                    }
                }}
                value={currentDisplayValue}
                autoComplete="off"
                data-lpignore={'true'}
                {...(props.componentProps as any)}
            />
        </Field>
    )
}
